import * as React from "react";

import { api } from "@repo/backend";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import { Checkbox } from "@repo/ui/components/checkbox";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import { DataTableColumnHeader } from "@repo/ui/components/data-table/data-table-column-header";
import { DataTableViewOptions } from "@repo/ui/components/data-table/data-table-view-options";
import { DateTimePicker } from "@repo/ui/components/date-time-picker";
import { Field, FieldGroup, FieldLabel, FieldSet } from "@repo/ui/components/field";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@repo/ui/components/hover-card";
import { Input } from "@repo/ui/components/input";
import { QRCode } from "@repo/ui/components/qr-code";
import { RelativeTimeCard } from "@repo/ui/components/relative-time-card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@repo/ui/components/sheet";
import { Switch } from "@repo/ui/components/switch";
import { useDataTable } from "@repo/ui/hooks/use-data-table";
import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { type StockFeatures, ColumnDef } from "@tanstack/react-table";
import { useAction } from "convex/react";
import { Copy, LoaderCircle, Pencil, PlusIcon, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { DataTable } from "@/components/data-table";
import { usePolling } from "@/hooks/use-polling";
import {
  createUploadTokenDirect,
  deleteUploadTokenDirect,
  listUploadTokensDirect,
  shouldUseDirectFanPicsWorker,
  toggleUploadTokenDirect,
  updateUploadTokenDirect,
  type TokenItem,
} from "@/lib/fan-pics-client";
import { useRoleAccess } from "@/lib/role-access";

export const Route = createFileRoute("/_authed/fan-pics/qr-codes")({
  head: () => ({ meta: [{ title: "QR Codes" }] }),
  component: RouteComponent,
});

const TOKEN_POLL_MS = 30_000;
const defaultExpiryMs = 1000 * 60 * 60 * 24 * 3;

function RouteComponent() {
  const confirm = useConfirmationDialog();
  const { isAdmin, isPhotographer } = useRoleAccess();
  const isReadOnly = isPhotographer || !isAdmin;
  const deleteUploadToken = useAction(api.fanPics.deleteUploadToken);
  const listUploadTokens = useAction(api.fanPics.listUploadTokens);
  const toggleUploadToken = useAction(api.fanPics.toggleUploadToken);
  const useDirectWorker = shouldUseDirectFanPicsWorker;
  const [tokens, setTokens] = React.useState<TokenItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [activeToggleId, setActiveToggleId] = React.useState<string | null>(null);

  const loadTokens = React.useCallback(
    async (options?: { silent?: boolean }) => {
      if (!options?.silent) setIsLoading(true);

      try {
        const result = useDirectWorker
          ? await listUploadTokensDirect()
          : await listUploadTokens({});

        if (!result.ok) {
          throw new Error(result.error);
        }

        setTokens(result.data.items);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not load QR codes.");
      } finally {
        if (!options?.silent) setIsLoading(false);
      }
    },
    [listUploadTokens, useDirectWorker],
  );

  React.useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  usePolling(() => loadTokens({ silent: true }), {
    enabled: true,
    intervalMs: TOKEN_POLL_MS,
  });

  const handleToggle = React.useCallback(
    async (tokenId: string) => {
      setActiveToggleId(tokenId);

      try {
        const result = useDirectWorker
          ? await toggleUploadTokenDirect({ tokenId })
          : await toggleUploadToken({ tokenId });

        if (!result.ok) {
          throw new Error(result.error);
        }

        await loadTokens({ silent: true });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not update QR code.");
      } finally {
        setActiveToggleId(null);
      }
    },
    [loadTokens, toggleUploadToken, useDirectWorker],
  );

  const handleDelete = React.useCallback(
    async (token: TokenItem) => {
      const confirmed = await confirm({
        title: "Delete QR Code",
        description: (
          <>
            Are you sure you want to delete the QR code for <strong>{token.sceneName}</strong>? This
            action cannot be undone.
          </>
        ),
        confirmText: "Delete",
        confirmButtonVariant: "destructive",
      });

      if (!confirmed) return;

      try {
        const result = useDirectWorker
          ? await deleteUploadTokenDirect(token.id)
          : await deleteUploadToken({ tokenId: token.id });

        if (!result.ok) {
          throw new Error(result.error);
        }

        toast.success("QR code deleted.");
        await loadTokens({ silent: true });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not delete QR code.");
      }
    },
    [confirm, deleteUploadToken, loadTokens, useDirectWorker],
  );

  const columns = React.useMemo<ColumnDef<StockFeatures, TokenItem>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Select row ${row.index + 1}`}
          />
        ),
        size: 32,
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "qrCode",
        accessorKey: "uploadUrl",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="QR Code" label="QR Code" />
        ),
        cell: ({ row }) => <FanPicsQrPreviewCell token={row.original} />,
      },
      {
        id: "sceneName",
        accessorKey: "sceneName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Scene" label="Scene" />
        ),
        size: 240,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.sceneName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.original.sceneId}</span>
          </div>
        ),
      },
      {
        id: "links",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Links" label="Links" />
        ),
        size: 260,
        meta: { grow: true },
        cell: ({ row }) => (
          <div className="w-full">
            <CopyableUrl value={row.original.uploadUrl} label="Upload" />
          </div>
        ),
      },
      {
        id: "expiresAt",
        accessorKey: "expiresAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Expires At" label="Expires At" />
        ),
        size: 220,
        cell: ({ row }) => <RelativeTimeCard date={new Date(row.original.expiresAt * 1000)} />,
      },
      {
        id: "status",
        accessorKey: "enabled",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" label="Status" />
        ),
        size: 160,
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Badge variant={row.original.enabled ? "default" : "outline"}>
              {row.original.enabled ? "enabled" : "disabled"}
            </Badge>
            <Switch
              checked={row.original.enabled}
              disabled={isReadOnly || activeToggleId === row.original.id}
              onCheckedChange={() => void handleToggle(row.original.id)}
            />
          </div>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 128,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <EditUploadTokenSheet
              disabled={isReadOnly}
              onUpdated={() => loadTokens({ silent: true })}
              token={row.original}
            />
            <Button
              size="icon-lg"
              variant="ghost"
              disabled={isReadOnly}
              onClick={() => void handleDelete(row.original)}
            >
              <Trash2 />
              <span className="sr-only">Delete</span>
            </Button>
          </div>
        ),
      },
    ],
    [activeToggleId, handleDelete, handleToggle, isReadOnly, loadTokens],
  );

  const table = useDataTable({ columns, data: tokens });

  return (
    <DataTable table={table} status="Exhausted" loadMore={() => {}}>
      <div className="mb-2 flex items-center justify-end gap-2">
        <Button onClick={() => void loadTokens()} variant="outline" size="sm" className="h-8">
          {isLoading ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}
          Refresh
        </Button>
        <CreateUploadTokenSheet disabled={isReadOnly} onCreated={() => loadTokens()} />
        <DataTableViewOptions table={table} className="ml-0!" />
      </div>
    </DataTable>
  );
}

function FanPicsQrPreviewCell({ token }: { token: TokenItem }) {
  return (
    <HoverCard>
      <HoverCardTrigger
        render={
          <button
            type="button"
            className="rounded-md transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            aria-label={`Preview upload destination for ${token.sceneName}`}
          />
        }
      >
        <QRCode data={token.uploadUrl} />
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-80 p-3">
        <div className="flex flex-col gap-2">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">{token.sceneName}</p>
            <p className="text-xs text-muted-foreground">Upload destination preview</p>
          </div>
          <CopyableUrl value={token.uploadUrl} label="Upload" />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function CreateUploadTokenSheet({
  disabled,
  onCreated,
}: {
  disabled: boolean;
  onCreated: () => void | Promise<void>;
}) {
  const createUploadToken = useAction(api.fanPics.createUploadToken);
  const useDirectWorker = shouldUseDirectFanPicsWorker;
  const [open, setOpen] = React.useState(false);
  const form = useForm({
    defaultValues: {
      expiresAt: new Date(Date.now() + defaultExpiryMs),
      sceneId: "",
      sceneName: "",
    },
    onSubmit: async ({ value }) => {
      const sceneId = value.sceneId.trim();
      const sceneName = value.sceneName.trim();

      if (!sceneId || !sceneName) {
        toast.error("Fill scene id and scene name first.");
        return;
      }

      try {
        const result = useDirectWorker
          ? await createUploadTokenDirect({
              expiresAt: Math.floor(value.expiresAt.getTime() / 1000),
              sceneId,
              sceneName,
            })
          : await createUploadToken({
              expiresAt: Math.floor(value.expiresAt.getTime() / 1000),
              sceneId,
              sceneName,
            });

        if (!result.ok) {
          throw new Error(result.error);
        }

        form.reset({
          expiresAt: new Date(Date.now() + defaultExpiryMs),
          sceneId: "",
          sceneName: "",
        });
        setOpen(false);
        toast.success("QR code created.");
        await onCreated();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not create QR code.");
      }
    },
  });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        render={(props) => (
          <Button {...props} disabled={disabled} size="sm" className="h-8">
            <PlusIcon />
            New QR Code
          </Button>
        )}
      >
        Open
      </SheetTrigger>
      <SheetContent>
        <form
          className="flex h-full flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <SheetHeader>
            <SheetTitle>Create QR Code</SheetTitle>
            <SheetDescription>
              Create a FanPics upload link for one scene. The QR code expires after the selected
              date and time.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-1 flex-col justify-between">
            <div className="grid flex-1 auto-rows-min gap-6 px-4">
              <FieldSet>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="fan-pics-scene-id">Scene ID</FieldLabel>
                    <form.Field name="sceneId">
                      {(field) => (
                        <Input
                          id="fan-pics-scene-id"
                          autoComplete="off"
                          placeholder="main-stage"
                          value={field.state.value}
                          onChange={(event) => field.handleChange(event.target.value)}
                        />
                      )}
                    </form.Field>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="fan-pics-scene-name">Scene name</FieldLabel>
                    <form.Field name="sceneName">
                      {(field) => (
                        <Input
                          id="fan-pics-scene-name"
                          autoComplete="off"
                          placeholder="Main Stage"
                          value={field.state.value}
                          onChange={(event) => field.handleChange(event.target.value)}
                        />
                      )}
                    </form.Field>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="fan-pics-expires-at">Expires at</FieldLabel>
                    <form.Field name="expiresAt">
                      {(field) => (
                        <DateTimePicker
                          id="fan-pics-expires-at"
                          value={field.state.value}
                          onValueChange={(date) => date && field.handleChange(date)}
                        />
                      )}
                    </form.Field>
                  </Field>
                </FieldGroup>
              </FieldSet>
            </div>
            <div className="mt-auto flex w-full gap-1 p-4">
              <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <Button className="flex-1" type="submit" disabled={!canSubmit || isSubmitting}>
                    {isSubmitting ? <LoaderCircle className="animate-spin" /> : null}
                    Create QR Code
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function EditUploadTokenSheet({
  disabled,
  onUpdated,
  token,
}: {
  disabled: boolean;
  onUpdated: () => void | Promise<void>;
  token: TokenItem;
}) {
  const updateUploadToken = useAction(api.fanPics.updateUploadToken);
  const useDirectWorker = shouldUseDirectFanPicsWorker;
  const [open, setOpen] = React.useState(false);
  const form = useForm({
    defaultValues: {
      expiresAt: new Date(token.expiresAt * 1000),
      sceneId: token.sceneId,
      sceneName: token.sceneName,
    },
    onSubmit: async ({ value }) => {
      try {
        const result = useDirectWorker
          ? await updateUploadTokenDirect({
              expiresAt: Math.floor(value.expiresAt.getTime() / 1000),
              sceneId: value.sceneId.trim(),
              sceneName: value.sceneName.trim(),
              tokenId: token.id,
            })
          : await updateUploadToken({
              expiresAt: Math.floor(value.expiresAt.getTime() / 1000),
              sceneId: value.sceneId.trim(),
              sceneName: value.sceneName.trim(),
              tokenId: token.id,
            });

        if (!result.ok) {
          throw new Error(result.error);
        }

        setOpen(false);
        toast.success("QR code updated.");
        await onUpdated();
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not update QR code.");
      }
    },
  });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        render={(props) => (
          <Button {...props} disabled={disabled} size="icon-lg" variant="secondary">
            <Pencil />
            <span className="sr-only">Edit</span>
          </Button>
        )}
      >
        Open
      </SheetTrigger>
      <SheetContent>
        <form
          className="flex h-full flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <SheetHeader>
            <SheetTitle>Edit QR Code</SheetTitle>
            <SheetDescription>
              Update scene details or expiration time for <strong>{token.sceneName}</strong>.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-1 flex-col justify-between">
            <div className="grid flex-1 auto-rows-min gap-6 px-4">
              <FieldSet>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor={`fan-pics-token-${token.id}-scene-id`}>
                      Scene ID
                    </FieldLabel>
                    <form.Field name="sceneId">
                      {(field) => (
                        <Input
                          id={`fan-pics-token-${token.id}-scene-id`}
                          autoComplete="off"
                          placeholder="main-stage"
                          value={field.state.value}
                          onChange={(event) => field.handleChange(event.target.value)}
                        />
                      )}
                    </form.Field>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={`fan-pics-token-${token.id}-scene-name`}>
                      Scene name
                    </FieldLabel>
                    <form.Field name="sceneName">
                      {(field) => (
                        <Input
                          id={`fan-pics-token-${token.id}-scene-name`}
                          autoComplete="off"
                          placeholder="Main Stage"
                          value={field.state.value}
                          onChange={(event) => field.handleChange(event.target.value)}
                        />
                      )}
                    </form.Field>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={`fan-pics-token-${token.id}-expires-at`}>
                      Expires at
                    </FieldLabel>
                    <form.Field name="expiresAt">
                      {(field) => (
                        <DateTimePicker
                          id={`fan-pics-token-${token.id}-expires-at`}
                          value={field.state.value}
                          onValueChange={(date) => date && field.handleChange(date)}
                        />
                      )}
                    </form.Field>
                  </Field>
                </FieldGroup>
              </FieldSet>
            </div>
            <div className="mt-auto flex w-full gap-1 p-4">
              <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <Button className="flex-1" type="submit" disabled={!canSubmit || isSubmitting}>
                    {isSubmitting ? <LoaderCircle className="animate-spin" /> : null}
                    Save changes
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function CopyableUrl({ value, label }: { value: string; label: string }) {
  return (
    <button
      type="button"
      className="flex w-full max-w-none cursor-pointer items-center gap-2 truncate rounded-md text-left transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      onClick={() =>
        void navigator.clipboard.writeText(value).then(() => toast.success(`${label} URL copied.`))
      }
    >
      <Copy className="size-3.5 shrink-0" />
      <span className="shrink-0 text-foreground">{label}</span>
      <span className="truncate font-mono">{value}</span>
    </button>
  );
}
